import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'; // 1. Import useEffect and useRef
import PropTypes from 'prop-types';
import { Col, Row } from 'reactstrap';
import { Form } from 'formik';
import { isEmpty } from 'lodash';
import classNames from 'classnames';
import CommonButton from '../../common/components/CommonButton';
import { CommonButtonSize } from '../../constants/CommonButtonVariants';
import FormPill from '../../common/components/formFields/FormPill';
import { FeatureGroups } from '../../constants/FeatureGroups';
import FormInputField from '../../common/components/formFields/FormInputField';
import { KEY_CODES } from '../../constants/KeyCodes';
import AddVehicleContentWrapper from '../AddVehicleContentWrapper';
import SettingsSectionHeader from '../../settings/SettingsSectionHeader';
import '../../assets/css/componentStyles/AddVehicle.css';
import '../../assets/css/componentStyles/FormValidation.css';

const { CAP_FEATURES_GROUP } = FeatureGroups;

const getSpecificationError = (validationErrors) =>
  validationErrors?.SPECIFICATION?.vehicleFeatures || validationErrors?.SPECIFICATION || null;

function AddFeaturesForm({
  form,
  allVehicleFeatures,
  className,
  header = 'Specification',
  description = 'Please select the relevant vehicle specification or add any additional specification.',
  customSectionTitle = 'Additional specification',
  addButtonLabel = 'Add',
  validationErrors = {},
  onValidate,
}) {
  const [ref, setRef] = useState(null);

  const vehicleFeaturesDictionary = useMemo(
    () => form.values?.vehicleFeatures?.vehicleFeaturesDictionary || [],
    [form.values?.vehicleFeatures?.vehicleFeaturesDictionary],
  );

  const vehicleFeaturesCustom = useMemo(
    () => form.values?.vehicleFeatures?.vehicleFeaturesCustom || [],
    [form.values?.vehicleFeatures?.vehicleFeaturesCustom],
  );

  const isAddingCustomFeatureDisabled = useMemo(
    () =>
      !form?.values?.newCustomFeature?.trim() ||
      vehicleFeaturesCustom.includes(form.values.newCustomFeature),
    [form?.values?.newCustomFeature, vehicleFeaturesCustom],
  );

  // const formError = validationErrors?.SPECIFICATION;

  // Helper: check if at least one feature is added
  const hasAnyFeature =
    (vehicleFeaturesDictionary && vehicleFeaturesDictionary.length > 0) ||
    (vehicleFeaturesCustom && vehicleFeaturesCustom.length > 0);

  const isFeatureOn = useCallback(
    (feature) =>
      vehicleFeaturesDictionary.some((item) => item === feature.id) ||
      (form.values.capFeatures &&
        Object.keys(form.values.capFeatures).some(
          (key) => form.values.capFeatures[key] === feature.value,
        )),
    [vehicleFeaturesDictionary, form.values.capFeatures],
  );

  const handleAddCapFeature = useCallback(
    (item) => {
      const currentFeatures = form.values.vehicleFeatures.vehicleFeaturesDictionary || [];
      const featureIndex = currentFeatures.indexOf(item.id);
      if (featureIndex > -1) {
        const newFeatures = [...currentFeatures];
        newFeatures.splice(featureIndex, 1);
        form.setFieldValue('vehicleFeatures.vehicleFeaturesDictionary', newFeatures);
      } else {
        form.setFieldValue('vehicleFeatures.vehicleFeaturesDictionary', [
          ...currentFeatures,
          item.id,
        ]);
      }
    },
    [form],
  );

  const handleCustomFeatureState = useCallback(
    (value) => {
      const currentCustom = form.values.vehicleFeatures.vehicleFeaturesCustom || [];
      const featureIndex = currentCustom.indexOf(value);

      if (featureIndex > -1) {
        const newCustom = [...currentCustom];
        newCustom.splice(featureIndex, 1);
        form.setFieldValue('vehicleFeatures.vehicleFeaturesCustom', newCustom);
      } else {
        form.setFieldValue('vehicleFeatures.vehicleFeaturesCustom', [...currentCustom, value]);
      }
    },
    [form],
  );

  const handleCustomFeatureAdd = useCallback(() => {
    const currentCustom = form.values.vehicleFeatures.vehicleFeaturesCustom || [];
    form.setFieldValue('vehicleFeatures.vehicleFeaturesCustom', [
      ...currentCustom,
      form.values.newCustomFeature,
    ]);
    form.setFieldValue('newCustomFeature', '');
    if (ref) ref.focus();
  }, [form, ref]);

  const handleEnterKeyAdd = useCallback(
    (e) => {
      if (!isAddingCustomFeatureDisabled && e.keyCode === KEY_CODES.ENTER) {
        e.preventDefault();
        handleCustomFeatureAdd();
      }
    },
    [isAddingCustomFeatureDisabled, handleCustomFeatureAdd],
  );

  const hasVehicleFeatures = useMemo(
    () => allVehicleFeatures && allVehicleFeatures.length > 0,
    [allVehicleFeatures],
  );

  const customFeatureStyle = useMemo(
    () => (isEmpty(vehicleFeaturesCustom) ? { paddingBottom: '34px' } : null),
    [vehicleFeaturesCustom],
  );

  const onValidateRef = useRef(onValidate);
  useEffect(() => {
    onValidateRef.current = onValidate;
  });

  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    onValidateRef.current();
  }, [vehicleFeaturesDictionary, vehicleFeaturesCustom]);

  const wrapperClassName = classNames(className);
  const specificationError = getSpecificationError(validationErrors);

  return (
    <AddVehicleContentWrapper className={wrapperClassName}>
      <SettingsSectionHeader header={header} description={description} />
      {specificationError && !hasAnyFeature && (
        <div className="field-error-message mt-2">
          {typeof specificationError === 'object'
            ? Object.values(specificationError)[0]
            : specificationError}
        </div>
      )}

      <Form autoComplete="off" role="form" aria-label="Vehicle features form">
        <Row className="mt-4">
          <Col md={12}>
            {hasVehicleFeatures && (
              <Row className="mb-1">
                <Col
                  md={12}
                  style={{
                    ...customFeatureStyle,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '2px',
                  }}
                  className="mt-2"
                  role="group"
                  aria-label="Available vehicle features"
                >
                  {allVehicleFeatures.map((item) => (
                    <FormPill
                      key={item.id}
                      feature={item.value}
                      isFeatureSelected={isFeatureOn(item)}
                      handleClick={() => handleAddCapFeature(item)}
                      deleteCallback={() => handleAddCapFeature(item)}
                      groupName={CAP_FEATURES_GROUP}
                      aria-label={`Toggle ${item.value} feature`}
                    />
                  ))}
                </Col>
              </Row>
            )}
          </Col>
        </Row>
        <Row>
          <Col md={3} className="mt-2">
            <FormInputField
              name="newCustomFeature"
              onKeyDown={handleEnterKeyAdd}
              setRef={setRef}
              labelVariant="bordered"
              label={customSectionTitle}
              form={form}
              aria-label="Enter custom feature name"
              aria-describedby="custom-feature-help"
            />
            <div id="custom-feature-help" className="sr-only">
              Press Enter or click Add Feature button to add custom specification
            </div>
          </Col>
          <Col md={3} className="mt-2">
            <CommonButton
              size={CommonButtonSize.SMALL}
              variant="outlined"
              handleClick={handleCustomFeatureAdd}
              disabled={isAddingCustomFeatureDisabled}
              label={addButtonLabel}
              type="button"
              aria-label="Add custom feature to the list"
              className="text-sm w-100"
            />
          </Col>
        </Row>
        <Row>
          <Col md={12} className="mt-3">
            {vehicleFeaturesCustom.length > 0 && (
              <div role="group" aria-label="Custom features">
                {vehicleFeaturesCustom.map((item) => (
                  <FormPill
                    key={item}
                    feature={item}
                    isFeatureSelected={() => false}
                    handleClick={() => handleCustomFeatureState(item)}
                    deleteCallback={() => handleCustomFeatureState(item)}
                    groupName="customFeatures"
                    aria-label={`Remove custom feature: ${item}`}
                  />
                ))}
              </div>
            )}
          </Col>
        </Row>
      </Form>
    </AddVehicleContentWrapper>
  );
}

AddFeaturesForm.propTypes = {
  form: PropTypes.shape({
    values: PropTypes.shape({
      vehicleFeatures: PropTypes.shape({
        vehicleFeaturesDictionary: PropTypes.array,
        vehicleFeaturesCustom: PropTypes.array,
      }),
      newCustomFeature: PropTypes.string,
      capFeatures: PropTypes.object,
    }),
    setFieldValue: PropTypes.func.isRequired,
  }).isRequired,
  allVehicleFeatures: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      value: PropTypes.string.isRequired,
    }),
  ),
  className: PropTypes.string,
  header: PropTypes.string,
  description: PropTypes.string,
  customSectionTitle: PropTypes.string,
  addButtonLabel: PropTypes.string,
  validationErrors: PropTypes.object,
  onValidate: PropTypes.func,
};

AddFeaturesForm.defaultProps = {
  allVehicleFeatures: [],
  className: '',
  header: 'Specification',
  description: 'Please add the relevant vehicle specification and any additional spec.',
  customSectionTitle: 'Add Custom Specification',
  addButtonLabel: '+ Add Feature',
  validationErrors: {},
  onValidate: () => {},
};

export default AddFeaturesForm;
